import { Account } from "models/Account";
import { Asset, AssetServiceInterval } from "models/Asset";
import { Project } from "models/Project";

export interface UpcomingAssetService {
    asset: Asset;
    interval: AssetServiceInterval;
    scheduledAt: Date;
}

export interface PendingHourGroup {
    projectId: string;
    projectName: string;
    workerAccountId: string;
    workerName: string;
    entryCount: number;
    minutes: number;
    oldestDate: Date;
}

export interface WorkerClientShare {
    clientKey: string;
    clientName: string;
    minutes: number;
    percentage: number;
}

export interface WorkerClientBalance {
    workerAccountId: string;
    workerName: string;
    totalMinutes: number;
    dominantPercentage: number;
    shares: WorkerClientShare[];
}

const accountNames = (accounts: Account[]) => new Map(accounts.map(account => [
    account.id,
    account.legal_name || account.id,
]));

const timestamp = (value?: string): number | null => {
    if (!value) return null;
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
};

export const upcomingAssetServices = (
    assets: Asset[],
    daysAhead: number,
    now = new Date(),
): UpcomingAssetService[] => {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const end = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() + Math.max(1, Math.floor(daysAhead)),
        23,
        59,
        59,
        999,
    ).getTime();

    return assets.flatMap(asset => (asset.service_intervals ?? []).flatMap(interval => {
        const scheduled = timestamp(interval.next_scheduled_at);
        if (!interval.enabled || scheduled === null || scheduled < start || scheduled > end) return [];
        return [{ asset, interval, scheduledAt: new Date(scheduled) }];
    })).sort((left, right) => left.scheduledAt.getTime() - right.scheduledAt.getTime()
        || left.asset.name.localeCompare(right.asset.name));
};

export const pendingHourGroups = (projects: Project[], accounts: Account[]): PendingHourGroup[] => {
    const names = accountNames(accounts);
    return projects.flatMap(project => (project.workers ?? []).flatMap(worker => {
        const entries = (worker.entries ?? []).filter(entry => entry.status === "pending_approval");
        if (entries.length === 0) return [];
        const oldest = Math.min(...entries.map(entry => timestamp(entry.date) ?? Number.MAX_SAFE_INTEGER));
        return [{
            projectId: project.id,
            projectName: project.name,
            workerAccountId: worker.account_id,
            workerName: names.get(worker.account_id) ?? worker.account_id,
            entryCount: entries.length,
            minutes: entries.reduce((total, entry) => total + entry.minutes, 0),
            oldestDate: new Date(oldest),
        }];
    })).sort((left, right) => left.oldestDate.getTime() - right.oldestDate.getTime()
        || left.workerName.localeCompare(right.workerName)
        || left.projectName.localeCompare(right.projectName));
};

export const workerClientBalances = (
    projects: Project[],
    accounts: Account[],
    periodDays: number,
    now = new Date(),
): WorkerClientBalance[] => {
    const names = accountNames(accounts);
    const start = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() - Math.max(1, Math.floor(periodDays)) + 1,
    ).getTime();
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).getTime();
    const workers = new Map<string, {
        name: string;
        totalMinutes: number;
        clients: Map<string, { name: string; minutes: number }>;
    }>();

    projects.forEach(project => {
        const clientIDs = [...(project.responsible ?? [])].sort();
        if (clientIDs.length === 0) return;
        const clientKey = clientIDs.join("|");
        const clientName = clientIDs.map(id => names.get(id) ?? id).join(" + ");
        (project.workers ?? []).forEach(worker => {
            const minutes = (worker.entries ?? [])
                .filter(entry => {
                    const date = timestamp(entry.date);
                    return entry.status !== "rejected" && date !== null && date >= start && date <= end;
                })
                .reduce((total, entry) => total + entry.minutes, 0);
            if (minutes <= 0) return;
            const aggregate = workers.get(worker.account_id) ?? {
                name: names.get(worker.account_id) ?? worker.account_id,
                totalMinutes: 0,
                clients: new Map<string, { name: string; minutes: number }>(),
            };
            const client = aggregate.clients.get(clientKey) ?? { name: clientName, minutes: 0 };
            client.minutes += minutes;
            aggregate.clients.set(clientKey, client);
            aggregate.totalMinutes += minutes;
            workers.set(worker.account_id, aggregate);
        });
    });

    return Array.from(workers.entries()).map(([workerAccountId, worker]) => {
        const shares = Array.from(worker.clients.entries())
            .map(([clientKey, client]) => ({
                clientKey,
                clientName: client.name,
                minutes: client.minutes,
                percentage: Math.round(client.minutes / worker.totalMinutes * 1000) / 10,
            }))
            .sort((left, right) => right.minutes - left.minutes || left.clientName.localeCompare(right.clientName));
        return {
            workerAccountId,
            workerName: worker.name,
            totalMinutes: worker.totalMinutes,
            dominantPercentage: shares[0]?.percentage ?? 0,
            shares,
        };
    }).sort((left, right) => right.dominantPercentage - left.dominantPercentage
        || right.totalMinutes - left.totalMinutes
        || left.workerName.localeCompare(right.workerName));
};
